<template>
<div class="flex flex-col w-full">
    <label class="block mr-12 first-letter:uppercase" for="licence-search-input">
        <slot></slot>
    </label>

    <div class="w-full mb-32">
        <div class="w-full rounded border border-primary-20 overflow-hidden">
            <div
                class="w-full px-16 py-8 rounded-t flex flex-wrap cursor-text"
                :class="showFilteredLicences ? ('border-b-2 border-primary-20') : (isFocussed ? 'border-primary-20' : '')"
                @click="focusInput"
                v-click-outside="removeFocus"
            >

                <div
                    v-for="(licence, index) in licences"
                    :key="licence.id"
                    class="flex-none bg-primary-5 font-semibold h-24 flex rounded-6 mr-8 mb-8 cursor-default">

                    <span class="pl-8 mr-8">{{ $t(licence.name) }}</span>
                    <button class="text-primary-80 hover:text-primary-80 trans-ease mr-4" @click="unselectLicence(index)">
                        <svg-icon type="cancel" class="max-w-16 max-h-16"></svg-icon>
                    </button>
                </div>

                <input
                    id="licence-search-input"
                    ref="licenceSearch"
                    type="search"
                    v-model="filterText"
                    class="flex-1 h-24 placeholder-primary-80 outline-none"
                    :placeholder="licences.length ? '' : $t('vdab.search_licence')"
                    @focus="setFocus"
                    @input="highlightLicence()"
                    @keydown.up.prevent="highlightPrevious"
                    @keydown.down.prevent="highlightNext"
                    @keydown.enter.prevent="selectLicence"
                    @keydown.delete="handleDelete"
                    @keydown.escape="resetFilterText"
                >
                <svg-icon v-show='!licences.length' type="search" class="max-h-16 max-w-16 my-auto ml-16"></svg-icon>
            </div>

            <div
                v-show="showFilteredLicences"
                class="w-full h-200 overflow-y-scroll relative" ref="container">

                <ul class="">
                    <li v-for="(licence, index) in filteredLicences" :key="licence.id" :ref="'licence-' + index">
                        <button
                            @mouseover="highlightLicence(index)"
                            @click.prevent.stop="selectLicence(licence)"
                            class="group px-16 h-40 leading-none flex items-center w-full"
                            data-licence="licence-option"
                            :class="[ highlightedLicence === index ? 'bg-primary text-white' : '' ]"
                        >
                            <span class="mr-auto">{{ $t(licence.name) }}</span>
                            <span class="ml-24 font-semibold" :class="[ highlightedLicence === index ? 'inline' : 'hidden' ]">{{ $t('general.select') }}</span>
                        </button>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import Loading from './Loading.vue';
import * as vdab from '../../services/VdabService';
import { normalizeJson } from '../../../shared/helpers';
import SvgIcon from './SvgIcon.vue';
import Input from '../forms/Input.vue';
import clickOutside from '../../mixins/clickOutside';

export default {
    components: {
        'loading': Loading,
        'svg-icon': SvgIcon,
        'simple-input': Input,
    },
    mixins: [
        clickOutside
    ],
    props: {
        currentLicences: {
            default: [],
            validator: value => Array.isArray(value),
        },
        working: Boolean,
    },
    data: function () {
        return {
            isFocussed: false,
            licences: [...this.currentLicences],
            filterText: '',
            highlightedLicence: 0,
            availableLicences: [
                { id:'A', name: 'vdab.licences.A' },
                { id:'A3', name: 'vdab.licences.A3' },
                { id:'B', name: 'vdab.licences.B' },
                { id:'B+E', name: 'vdab.licences.B+E' },
                { id:'C', name: 'vdab.licences.C' },
                { id:'C+E', name: 'vdab.licences.C+E' },
                { id:'C1', name: 'vdab.licences.C1' },
                { id:'C1+E', name: 'vdab.licences.C1+E' },
                { id:'D', name: 'vdab.licences.D' },
                { id:'D+E', name: 'vdab.licences.D+E' },
                { id:'D1', name: 'vdab.licences.D1' },
                { id:'D1+E', name: 'vdab.licences.D1+E' },
                { id:'G', name: 'vdab.licences.G' },
            ],
        }
    },
    computed: {
        query () {
            return this.filterText.trim().toLowerCase();
        },
        filteredLicences () {
            //avoid showing licences already selected
            return this.availableLicences.filter(licence => {
                return !this.licences.includes(licence) &&
                this.$t(licence.name).trim().toLowerCase().includes(this.query);
            });
        },
        showFilteredLicences () {
            return this.isFocussed &&
                this.filteredLicences.length > 0;
        },
    },
    methods: {
        blurHandle (evt) {
            if (!evt.relatedTarget ||
                !evt.relatedTarget.getAttribute('data-licence')) {
                this.removeFocus();
            }
        },
        setFocus () {
            this.isFocussed = true;
        },
        removeFocus () {
            this.isFocussed = false;
        },
        resetFilterText () {
            this.filterText = '';
        },
        selectLicence (licence = null) {
            this.licences.push((licence.type !== 'keydown') ?
                licence :
                this.filteredLicences[this.highlightedLicence]);
            this.highlightedLicence = 0;
            this.$refs.container.scrollTop = 0;
            this.$refs.licenceSearch.focus();
            this.$emit('select', this.licences);
        },
        unselectLicence (index) {
            this.licences.splice(index, 1);
        },
        highlightLicence (index) {
            this.highlightedLicence = index != null ? index : 0;
        },
        highlightPrevious () {
            this.highlightedLicence = (this.highlightedLicence - 1) < 0 ?
                this.filteredLicences.length - 1 :
                this.highlightedLicence - 1;

            this.scrollIntoView();
        },
        highlightNext () {
            this.highlightedLicence =
                (this.highlightedLicence + 1 ) == this.filteredLicences.length ?
                0 :
                this.highlightedLicence + 1;

            this.scrollIntoView();
        },
        scrollIntoView () {
            let container = this.$refs.container;
            let el = this.$refs['licence-' + this.highlightedLicence][0] || this.$refs['licence-' + this.highlightedLicence];

            if (el.offsetTop < container.scrollTop) {
                container.scrollTop = el.offsetTop;
            }

            if (el.offsetTop + el.clientHeight > container.clientHeight + container.scrollTop) {
                container.scrollTop = el.offsetTop - (container.clientHeight - el.clientHeight);
            }
        },
        handleDelete (e) {
            if (e.code != 'Backspace') {
                return;
            }

            if (!this.filterText.length) {
                this.licences.pop();
            }
        },
        handleSubmit () {
            this.$emit('update', this.licences);
        },
        focusInput () {
            this.$refs.licenceSearch.focus();
        },
    },
    watch: {
        licences (newLicences, oldLicences) {
            this.$emit('select', newLicences);
        },
    },
}
</script>
